/**
 * Crypto Currency + Chain Selector Modal
 * Searchable list of crypto+chain combinations with compound icons (crypto icon + chain badge).
 * Smart search ranking: exact code > starts with > popular > other.
 * Uses API data if provided, falls back to static CRYPTO_CHAIN_COMBOS.
 */

import { useState, useMemo, useCallback, useRef } from 'react';
import { useT } from '../../i18n';
import { CloseButton } from '../ui/CloseButton';
import { SearchInput } from '../ui/SearchInput';
import { NRLogo } from '../ui/NRLogo';
import {
	CRYPTO_CHAIN_COMBOS,
	getCryptoIconUrl,
	getChainInfo,
	getChainIconUrl,
	CRYPTO_NAMES,
	type CryptoChainCombo,
} from '../../data/cryptoData';

const POPULAR_CRYPTOS = ['BTC', 'ETH', 'SOL'];

/** Image component with loading state and error fallback */
function CryptoImage({ src, alt, size, className, style }: {
	src: string;
	alt: string;
	size: number;
	className?: string;
	style?: React.CSSProperties;
}) {
	const [loaded, setLoaded] = useState(false);
	const [error, setError] = useState(false);
	const currentSrc = useRef(src);

	// Reset state when src changes, using ref to avoid race with onLoad
	if (currentSrc.current !== src) {
		currentSrc.current = src;
		setLoaded(false);
		setError(false);
	}

	const handleLoad = useCallback(() => setLoaded(true), []);
	const handleError = useCallback(() => setError(true), []);

	return (
		<div className={className} style={{ ...style, width: size, height: size, position: 'relative' }}>
			{/* Placeholder shown while loading */}
			{!loaded && !error && (
				<div
					className="absolute inset-0 rounded-full animate-pulse"
					style={{ background: 'var(--t-pill-bg, #333)' }}
				/>
			)}
			{/* Fallback for broken images */}
			{error ? (
				<div
					className="absolute inset-0 rounded-full flex items-center justify-center text-[10px] font-bold"
					style={{ background: 'var(--t-pill-bg, #333)', color: 'var(--t-text-muted)' }}
				>
					{alt.slice(0, 3)}
				</div>
			) : (
				<img
					src={src}
					alt={alt}
					width={size}
					height={size}
					onLoad={handleLoad}
					onError={handleError}
					className={`rounded-full ${loaded ? 'opacity-100' : 'opacity-0'}`}
					style={{ width: size, height: size, transition: 'opacity 0.15s ease-in' }}
				/>
			)}
		</div>
	);
}

export interface CryptoCurrencyModalProps {
	onClose: () => void;
	onSelect: (code: string, chain: string) => void;
	/** Crypto data from API config — takes precedence over static fallback */
	cryptos?: Array<{
		code: string;
		name?: string;
		networks?: Array<{ code: string; id?: string; name?: string }>;
	}>;
	/** Base URL for static assets (crypto icons) */
	assetBaseUrl?: string;
	/** Currently selected crypto code */
	selectedCode?: string;
	/** Currently selected chain/network */
	selectedChain?: string;
}

export function CryptoCurrencyModal({ onClose, onSelect, cryptos, assetBaseUrl = '', selectedCode, selectedChain }: CryptoCurrencyModalProps) {
	const t = useT();
	const [search, setSearch] = useState('');

	// Build flat combo list from API data or fall back to static data
	const comboList = useMemo<CryptoChainCombo[]>(() => {
		if (!cryptos || cryptos.length === 0) return CRYPTO_CHAIN_COMBOS;

		const list: CryptoChainCombo[] = [];
		for (const c of cryptos) {
			const networks = c.networks || [];
			// Get name from API, fallback to CRYPTO_NAMES lookup, then code
			const cryptoName = c.name || CRYPTO_NAMES[c.code] || c.code;
			if (networks.length === 0) {
				// No networks listed — add a single entry with the code as chain
				list.push({ code: c.code, name: cryptoName, chain: c.code, popular: POPULAR_CRYPTOS.includes(c.code) });
			} else {
				for (const net of networks) {
					const chainCode = net.code || net.id || net.name || c.code;
					list.push({
						code: c.code,
						name: cryptoName,
						chain: chainCode,
						chainName: net.name,
						popular: POPULAR_CRYPTOS.includes(c.code),
					});
				}
			}
		}
		return list;
	}, [cryptos]);

	// Check if an item is currently selected
	const isSelected = useCallback((code: string, chain: string) => {
		return selectedCode === code && selectedChain === chain;
	}, [selectedCode, selectedChain]);

	// Filtered and ranked results
	const filtered = useMemo(() => {
		const raw = search.trim().toLowerCase();

		// If no search, put selected item first
		if (!raw) {
			if (selectedCode && selectedChain) {
				const selected = comboList.find(c => c.code === selectedCode && c.chain === selectedChain);
				if (selected) {
					const rest = comboList.filter(c => !(c.code === selectedCode && c.chain === selectedChain));
					return [selected, ...rest];
				}
			}
			return comboList;
		}

		const terms = raw.split(/\s+/);
		const scored: Array<{ item: CryptoChainCombo; score: number }> = [];

		for (const c of comboList) {
			const chain = getChainInfo(c.chain);
			const chainName = chain ? chain.name.toLowerCase() : '';
			const chainShort = chain?.shortName?.toLowerCase() || '';
			const chainCode = c.chain.toLowerCase();
			const code = c.code.toLowerCase();
			const name = c.name.toLowerCase();
			const fields = [code, name, chainName, chainShort, chainCode];

			// All search terms must match at least one field
			const allMatch = terms.every((t) => fields.some((f) => f.includes(t)));
			if (!allMatch) continue;

			// Scoring: exact code match > starts with > chain match bonus > popular
			let score = 0;
			if (code === raw) score += 100;
			else if (code.startsWith(terms[0])) score += 50;
			if (c.popular) score += 10;
			if (
				terms.length > 1 &&
				(chainName.includes(terms[1]) ||
					chainShort.includes(terms[1]) ||
					chainCode.includes(terms[1]))
			) {
				score += 25;
			}
			scored.push({ item: c, score });
		}

		scored.sort((a, b) => b.score - a.score);
		return scored.map((s) => s.item);
	}, [comboList, search, selectedCode, selectedChain]);

	return (
		<>
			{/* Modal header — 128px with blurred bg */}
			<div className="relative shrink-0" style={{ height: 128, background: 'var(--t-header-bg)', backdropFilter: 'blur(var(--t-header-blur))', WebkitBackdropFilter: 'blur(var(--t-header-blur))' }}>
				<div className="absolute" style={{ left: 12, top: 20 }}>
					<NRLogo width={46} />
				</div>
				<p className="absolute text-[16px] text-center truncate" style={{ top: 28, left: 64, right: 64, color: 'var(--t-text)', lineHeight: '24px' }}>
					{t('modal.selectCrypto')}
				</p>
				<div className="absolute" style={{ right: 12, top: 20 }}>
					<CloseButton onClick={onClose} />
				</div>
				<div className="absolute" style={{ bottom: 8, left: 12, right: 12 }}>
					<SearchInput value={search} onChange={setSearch} inline />
				</div>
			</div>
			<div className="flex-1 overflow-y-auto custom-scroll px-3">
				{filtered.map((c, i) => {
					const chain = getChainInfo(c.chain);
					const chainName = chain?.name || c.chainName || c.chain;
					const itemSelected = isSelected(c.code, c.chain);
					return (
						<button
							key={`${c.code}-${c.chain}`}
							onClick={() => onSelect(c.code, c.chain)}
							className="flex items-center w-full card-press hover-item modal-item-in"
							style={{ gap: 16, animationDelay: `${Math.min(i * 45, 350)}ms` }}
						>
							{/* Compound icon: crypto icon + chain badge */}
							<div className="relative shrink-0" style={{ width: 40, height: 40 }}>
								<div
									className="absolute top-0 left-0"
									style={{ background: '#ffffff', borderRadius: '50%' }}
								>
									<CryptoImage
										src={getCryptoIconUrl(c.code, assetBaseUrl)}
										alt={c.code}
										size={32}
									/>
								</div>
								<div
									className="absolute"
									style={{
										bottom: 0,
										right: 0,
										border: '1.5px solid var(--t-chain-border, var(--t-surface, #1C1C22))',
										borderRadius: '50%',
										background: '#ffffff',
									}}
								>
									<CryptoImage
										src={getChainIconUrl(c.chain, assetBaseUrl)}
										alt={c.chain}
										size={16}
									/>
								</div>
							</div>
							<div className="flex-1 flex items-center" style={{ borderBottom: '1px solid var(--t-card-border)', paddingTop: 20, paddingBottom: 20, gap: 10 }}>
								<span className="flex-1 text-[18px] text-left" style={{ color: 'var(--t-text)', lineHeight: '20px' }}>
									{c.code}
								</span>
								<span className="text-[15px]" style={{ color: 'var(--t-text-secondary)' }}>
									{chainName}
								</span>
								{c.popular && (
									<span className="text-[11px] font-medium" style={{ border: '1.5px solid var(--t-card-border)', borderRadius: 100, paddingLeft: 8, paddingRight: 8, color: 'var(--t-text-secondary)', lineHeight: '20px', letterSpacing: '0.22px' }}>
										{t('modal.popular')}
									</span>
								)}
								{itemSelected && (
									<div style={{ width: 40, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
										<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
											<path
												d="M13.5 4.5L6 12L2.5 8.5"
												stroke="var(--t-success, #4CD964)"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									</div>
								)}
							</div>
						</button>
					);
				})}
				{filtered.length === 0 && (
					<div className="text-center py-10 text-[14px]" style={{ color: 'var(--t-text-muted)' }}>
						{t('modal.noCurrencies')}
					</div>
				)}
			</div>
		</>
	);
}
